import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, QrCode, Trophy, Users } from "lucide-react";

export const Landing = () => {
  return (
    <div className="min-h-screen bg-obsidian" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-graphite/80 backdrop-blur-xl border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/app-icon.png" 
                alt="CamPassed" 
                className="w-8 h-8 rounded-lg shadow-lg shadow-electric-ice-blue/20"
              />
              <span className="text-xl font-bold tracking-[-0.02em] text-cloud-white">
                CamPassed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button 
                  variant="ghost" 
                  className="text-soft-silver hover:text-cloud-white hover:bg-white/[0.05]"
                  data-testid="landing-login"
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/signup">
                <Button 
                  className="bg-gradient-to-r from-electric-ice-blue to-blue-500 text-obsidian font-semibold hover:opacity-90"
                  data-testid="landing-signup"
                >
                  Get CamPassed
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 dark:opacity-100 opacity-50">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-electric-ice-blue/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cool-mint/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-cloud-white leading-tight">
            Get CamPassed
            <br />
            <span className="text-electric-ice-blue">or Get Left Out</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-soft-silver max-w-2xl mx-auto">
            Your pass to the hottest campus events. Event management, guest passes, QR check-in, and social discovery for fraternities, sororities, and campus organizations.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-electric-ice-blue to-blue-500 text-obsidian font-semibold hover:opacity-90 px-8"
                data-testid="hero-get-started"
              >
                Get CamPassed
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/discover">
              <Button 
                size="lg"
                variant="outline"
                className="border-[var(--border-glass)] text-cloud-white hover:bg-[var(--border-subtle)]"
                data-testid="hero-discover"
              >
                Discover events
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Calendar,
                title: "Event Command Center",
                description: "Manage events, track attendance, and control capacity in real-time."
              },
              {
                icon: QrCode,
                title: "QR Guest Passes",
                description: "Unique QR codes for every guest. Fast check-in, zero hassle."
              },
              {
                icon: Users,
                title: "Guest Management",
                description: "Invite chains, waitlists, and +1 tracking all in one place."
              },
              {
                icon: Trophy,
                title: "Campus Passport",
                description: "Badges, leaderboards, and shareable milestones."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-graphite rounded-xl border border-[var(--border-subtle)] p-6 hover:border-[var(--border-glass)] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-electric-ice-blue" />
                </div>
                <h3 className="text-lg font-medium text-cloud-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-soft-silver">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-cloud-white">
            Ready to level up your events?
          </h2>
          <p className="mt-4 text-soft-silver">
            Join campuses across the country using CamPassed.
          </p>
          <Link to="/signup">
            <Button 
              size="lg"
              className="mt-8 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium px-8"
              data-testid="cta-get-started"
            >
              Get started today
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[var(--border-subtle)]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-electric-ice-blue flex items-center justify-center">
              <span className="text-white font-bold text-xs">C</span>
            </div>
            <span className="text-sm text-soft-silver">© 2025 CamPassed</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-soft-silver">
            <Link to="/login" className="hover:text-cloud-white transition-colors">
              Organization Login
            </Link>
            <Link to="/platform" className="hover:text-cloud-white transition-colors">
              Platform Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
